import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, CalendarClock, MessageSquare } from "lucide-react";
import { track } from "@/lib/analytics";

import ProgramaSidebar from "./programa/ProgramaSidebar";
import LessonViewer from "./programa/LessonViewer";

export default function ProgramaView() {
  const { programId } = useParams();
  const { user } = useAuth();
  const [view, setView] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/alumno/programs/${programId}`)
      .then((r) => {
        setView(r.data);
        const nextId = r.data.progress.next_lesson_id || r.data.modules?.[0]?.lessons?.[0]?.id;
        if (nextId) setActiveLessonId(nextId);
        track("program_started", { program_id: programId });
      })
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
    api.get("/alumno/live-sessions/upcoming").then((r) => {
      setUpcoming((r.data || []).filter((s) => s.is_future && s.program_id === programId).slice(0, 3));
    }).catch(() => {});
  }, [programId]);

  useEffect(() => {
    if (!activeLessonId) return;
    setLoadingLesson(true);
    api.get(`/alumno/lessons/${activeLessonId}`)
      .then((r) => setLesson(r.data))
      .catch(() => toast.error("No se pudo cargar la lección"))
      .finally(() => setLoadingLesson(false));
  }, [activeLessonId]);

  const refreshProgress = async () => {
    const r = await api.get(`/alumno/programs/${programId}`);
    setView(r.data);
  };

  const markCompleted = async () => {
    try {
      const r = await api.post("/alumno/progress", { lesson_id: activeLessonId, status: "completed" });
      toast.success("Lección completada");
      track("lesson_completed", { lesson_id: activeLessonId, program_id: programId });
      if (r.data.certificate_folio) {
        toast.success(`¡Programa completado! Certificado ${r.data.certificate_folio} emitido.`);
        track("program_completed", { program_id: programId, folio: r.data.certificate_folio });
      }
      await refreshProgress();
      const next = r.data.progress.next_lesson_id;
      if (next && next !== activeLessonId) setActiveLessonId(next);
      else if (r.data.certificate_folio) nav("/app/alumno/certificados");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (notFound) return (
    <AlumnoLayout user={user}>
      <div className="text-center py-24">
        <p className="text-xl text-white">Programa no encontrado</p>
        <Link to="/app/alumno/programas" className="mt-4 inline-block text-[#93C5FD]">Volver</Link>
      </div>
    </AlumnoLayout>
  );

  if (!view) return <AlumnoLayout user={user}><p className="text-gray-400">Cargando...</p></AlumnoLayout>;

  return (
    <AlumnoLayout user={user}>
      <div>
        <Link to="/app/alumno/programas" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-3">
          <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Mis programas
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">{view.program.format} · {view.program.code}</p>
            <h1 className="mt-1 text-2xl md:text-3xl font-bold text-white font-display" data-testid="program-view-title">{view.program.title}</h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to={`/app/alumno/programas/${programId}/foro`}
              data-testid="program-forum-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-700 text-gray-200 hover:bg-white/5 text-sm font-semibold"
            >
              <MessageSquare className="w-4 h-4" /> Foro
            </Link>
            <div className="w-40 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#3B82F6]" style={{ width: `${view.progress.percent}%` }} />
            </div>
            <span className="font-mono text-sm text-white" data-testid="program-progress-percent">{view.progress.percent}%</span>
          </div>
        </div>

        <div className="mt-6 grid lg:grid-cols-4 gap-6">
          <ProgramaSidebar
            modules={view.modules}
            progress={view.progress}
            activeLessonId={activeLessonId}
            onSelect={setActiveLessonId}
          />

          <div className="lg:col-span-3 space-y-6">
            {loadingLesson || !lesson ? (
              <div className="h-96 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />
            ) : (
              <LessonViewer lesson={lesson} onCompleted={markCompleted} />
            )}

            {upcoming.length > 0 && (
              <aside className="p-4 bg-[#111827] border border-gray-800 rounded-xl" data-testid="program-upcoming-live">
                <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold flex items-center gap-1">
                  <CalendarClock className="w-3.5 h-3.5" aria-hidden="true" /> Próximas sesiones live de tu cohorte
                </p>
                <ul className="mt-3 space-y-2 text-sm">
                  {upcoming.map((s) => (
                    <li key={s.lesson_id} className="text-gray-300 font-mono">
                      {new Date(s.starts_at).toLocaleString("es-MX")} — {s.title}
                    </li>
                  ))}
                </ul>
              </aside>
            )}
          </div>
        </div>
      </div>
    </AlumnoLayout>
  );
}
